import {
  GrowthCell as GrowthCellType,
  PercentCell as PercentCellType,
} from "@/financeModels/owasco/types";
import {
  formatDecimalPercent,
  numberToPercentInput,
  percentInputToNumber,
} from "@/utils/format";
import { NumberInput } from "@mantine/core";
import { PrimitiveAtom, useAtom } from "jotai";
import React, { memo, useState } from "react";
import { Stub } from "./cell/Stub";
import { Wrapper } from "./cell/Wrapper";
import { useTabCoordinates } from "./hooks/useTabCoordinates";

export type PercentCellProps = {
  tabX: number;
  tabY: number;
  atom: PrimitiveAtom<PercentCellType> | PrimitiveAtom<GrowthCellType>;
  ta?: "left" | "right" | "center";
  w?: number;
  h?: number;
  style?: React.CSSProperties;
  justifyContent?: "left" | "right" | "center";
  tooltip?: string;
};

export const PercentCell = memo(
  ({
    tabX,
    tabY,
    atom,
    ta,
    w,
    h,
    justifyContent = "left",
    style = {},
  }: PercentCellProps) => {
    const [cell, setCell] = useAtom(atom as PrimitiveAtom<PercentCellType>);
    const [inputValue, setInputValue] = useState(cell.value);
    const [isEditing, setIsEditing] = useState(false);

    const { id, moveFocus } = useTabCoordinates({ tabX, tabY });

    const { isLocked, isOverrided, isBlank, tooltip } = cell;
    const isEditMode = !isLocked && isEditing;

    const resetCell = () => {
      setCell((c) => ({ ...c, value: null }));
    };

    return (
      <Wrapper w={w} h={h} id={id} style={style}>
        {!isEditMode && (
          <Stub
            resetCell={resetCell}
            ta={ta}
            moveFocus={moveFocus}
            label={
              <span
                style={{
                  color:
                    Number(cell.value) < 0
                      ? "var(--mantine-color-red-9)"
                      : undefined,
                  whiteSpace: "pre",
                }}
              >
                {cell.value !== -Infinity && Number(cell.value) < 0
                  ? formatDecimalPercent(cell.value).replace("-", "(") + ")"
                  : formatDecimalPercent(cell.value)}
              </span>
            }
            enableEditing={() => {
              setInputValue(null);
              setIsEditing(true);
            }}
            locked={isLocked}
            blank={isBlank}
            overrided={isOverrided}
            animationKey={cell.value?.toString()}
            justifyContent={justifyContent}
            tooltip={tooltip}
          />
        )}
        {isEditMode && (
          <NumberInput
            value={numberToPercentInput(inputValue)}
            onChange={(value) => setInputValue(percentInputToNumber(value))}
            autoFocus
            radius={0}
            size="sm"
            w={"100%"}
            rightSection="%"
            min={0}
            step={0.01}
            hideControls
            onBlur={() => {
              setIsEditing(false);
              setCell((c) => ({ ...c, value: inputValue }));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setCell((c) => ({ ...c, value: inputValue }));
                setIsEditing(false);
                moveFocus("down");
              }
            }}
          />
        )}
      </Wrapper>
    );
  },
);
